import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, MenuItem, IconButton, Button,
    CircularProgress, Tooltip, Autocomplete, Chip,
    ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import CakeIcon from "@mui/icons-material/Cake";
import TodayIcon from "@mui/icons-material/Today";
import EventIcon from "@mui/icons-material/Event";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";

const COLOR = SECTION_COLORS.internalJobs;
const fsx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Build selector JSON from builder state.
 *
 * Birthday rows have a special `_birthdayMode` key:
 *   - "today"  → top-level { birthdate_today: true }
 *   - "fixed"  → normal AST leaf { field, operator, value: "MM-DD" }
 *
 * All other rows are normal AST leaves, wrapped in { [combinator]: [...] }.
 */
function dominantKey(items, fallback = "and") {
    const joiners = items.slice(1).map((i) => i.joiner || "and");
    const orCount = joiners.filter((j) => j === "or").length;
    return joiners.length > 0 && orCount > joiners.length / 2 ? "or" : fallback;
}

function serializeGroup(conditions) {
    const valid = (conditions || []).filter((c) => c.field);
    if (valid.length === 0) return null;
    // Single condition: unwrap to avoid pointless { "and": [item] } nesting
    if (valid.length === 1) {
        return { field: valid[0].field, operator: valid[0].operator, value: valid[0].value };
    }
    const key = dominantKey(valid);
    // No _joiner metadata — inner items all use the same group combinator
    return { [key]: valid.map((c) => ({ field: c.field, operator: c.operator, value: c.value })) };
}

export function buildSelectorJson(combinator, rows) {
    if (!rows || rows.length === 0) return {};

    const birthdayRows = rows.filter((r) => r.field === "customer.birthdate" && r._birthdayMode === "today");
    const astRows = rows.filter((r) => !(r.field === "customer.birthdate" && r._birthdayMode === "today"));

    if (birthdayRows.length > 0 && astRows.length === 0) return { birthdate_today: true };

    const rootKey = dominantKey(astRows, combinator || "and");
    const children = astRows
        .filter((r) => r.type === "group" ? (r.conditions || []).some((c) => c.field) : r.field)
        .map((r) => {
            if (r.type === "group") return serializeGroup(r.conditions);
            // Plain condition — no _joiner metadata in output
            return { field: r.field, operator: r.operator, value: r.value };
        })
        .filter(Boolean);

    const result = {};
    if (children.length > 0) result[rootKey] = children;
    if (birthdayRows.length > 0) result.birthdate_today = true;
    return result;
}

/**
 * Parse existing selector JSON back into builder rows.
 * Handles:
 *   { birthdate_today: true }          → birthday-today row
 *   { and/or: [...] }                  → AST array rows
 *   { field, operator, value }         → single flat leaf
 */
export function parseSelectorJson(selector) {
    const defaultResult = { combinator: "and", rows: [] };
    if (!selector || typeof selector !== "object" || Array.isArray(selector)) return defaultResult;
    const keys = Object.keys(selector);
    if (keys.length === 0) return defaultResult;

    const rows = [];
    let combinator = "and";

    // Handle birthdate_today flag
    if (selector.birthdate_today === true) {
        rows.push({
            id: 0,
            field: "customer.birthdate",
            operator: "eq",
            value: "",
            _birthdayMode: "today",
        });
    }

    // Handle AST combinators — support both flat conditions and nested groups
    for (const comb of ["and", "or"]) {
        if (comb in selector && Array.isArray(selector[comb])) {
            combinator = comb;
            selector[comb].forEach((item, idx) => {
                const isGroup = !('field' in item) && ('and' in item || 'or' in item);
                if (isGroup) {
                    const innerComb = ['and','or'].find((k) => k in item) || 'and';
                    rows.push({
                        id: `g_${rows.length}_${idx}`,
                        type: 'group',
                        joiner: idx > 0 ? (item._joiner || comb) : undefined,
                        conditions: (item[innerComb] || [])
                            .filter((c) => c && 'field' in c)
                            .map((c, i) => ({
                                id: `gc_${idx}_${i}`,
                                type: 'condition',
                                field: c.field || '',
                                operator: c.operator || 'eq',
                                value: c.value ?? '',
                                joiner: i > 0 ? (c._joiner || innerComb) : undefined,
                            })),
                    });
                } else {
                    rows.push({
                        id: rows.length + idx,
                        type: 'condition',
                        field: item.field ?? '',
                        operator: item.operator ?? item.op ?? 'eq',
                        value: item.value ?? '',
                        joiner: idx > 0 ? (item._joiner || comb) : undefined,
                        _birthdayMode: item.field === 'customer.birthdate' ? 'fixed' : undefined,
                    });
                }
            });
        }
    }

    if (rows.length > 0) return { combinator, rows };

    // Handle "not"
    if ("not" in selector) {
        const inner = selector.not;
        if (inner && typeof inner === "object" && "field" in inner) {
            return {
                combinator: "not",
                rows: [{
                    id: 0,
                    field: inner.field ?? "",
                    operator: inner.operator ?? "eq",
                    value: inner.value ?? "",
                    _birthdayMode: inner.field === "customer.birthdate" ? "fixed" : undefined,
                }],
            };
        }
        return defaultResult;
    }

    // Handle single flat leaf
    if ("field" in selector) {
        return {
            combinator: "and",
            rows: [{
                id: 0,
                field: selector.field ?? "",
                operator: selector.operator ?? selector.op ?? "eq",
                value: selector.value ?? "",
                _birthdayMode: selector.field === "customer.birthdate" ? "fixed" : undefined,
            }],
        };
    }

    return defaultResult;
}

// ── BirthdaySelector ───────────────────────────────────────────────────────────
/**
 * Dedicated UI for customer.birthdate.
 * Replaces the generic Operator + ValueEditor pair with a two-mode toggle.
 */
function BirthdaySelector({ row, onUpdate }) {
    const mode = row._birthdayMode || "today";

    const handleModeChange = (_, val) => {
        if (!val) return;
        onUpdate({
            _birthdayMode: val,
            operator: "eq",
            value: val === "today" ? "" : (row.value || ""),
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5} mt={0.5}>
            {/* Mode toggle */}
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <CakeIcon sx={{ fontSize: 16, color: COLOR.main }} />
                <Typography fontSize={12} fontWeight={600} color="text.secondary">
                    Type de ciblage anniversaire :
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={mode}
                    onChange={handleModeChange}
                    sx={{ height: 28 }}
                >
                    <ToggleButton
                        value="today"
                        sx={{ fontSize: 11, fontWeight: 700, px: 1.5, textTransform: "none", gap: 0.5 }}
                    >
                        <TodayIcon sx={{ fontSize: 13 }} /> Aujourd'hui (dynamique)
                    </ToggleButton>
                    <ToggleButton
                        value="fixed"
                        sx={{ fontSize: 11, fontWeight: 700, px: 1.5, textTransform: "none", gap: 0.5 }}
                    >
                        <EventIcon sx={{ fontSize: 13 }} /> Date fixe (MM-JJ)
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Mode output */}
            {mode === "today" ? (
                <Box
                    sx={{
                        px: 2, py: 1, borderRadius: 2, bgcolor: "#f0fdf4",
                        border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: 1,
                    }}
                >
                    <TodayIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                    <Typography fontSize={12} color="#15803d" fontWeight={500}>
                        Sélectionne les clients dont l'anniversaire est{" "}
                        <strong>aujourd'hui</strong>. La date est calculée dynamiquement
                        à chaque exécution du job.
                    </Typography>
                </Box>
            ) : (() => {
                // Convert stored MM-DD → YYYY-MM-DD for the date picker display
                const storedMmDd = row.value ?? "";
                const currentYear = new Date().getFullYear();
                const pickerValue = storedMmDd.length === 5 && storedMmDd[2] === "-"
                    ? `${currentYear}-${storedMmDd}`   // "04-20" → "2026-04-20"
                    : storedMmDd;                       // pass-through if already full or empty

                return (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                        <TextField
                            size="small"
                            type="date"
                            label="Date anniversaire"
                            InputLabelProps={{ shrink: true }}
                            value={pickerValue}
                            onChange={(e) => {
                                // e.target.value is "YYYY-MM-DD" — extract only MM-DD
                                const full = e.target.value; // e.g. "2026-04-20"
                                const mmDd = full.length === 10 ? full.slice(5) : full; // → "04-20"
                                onUpdate({ value: mmDd });
                            }}
                            helperText={
                                storedMmDd
                                    ? `Valeur stockée : ${storedMmDd} (MM-JJ, année ignorée)`
                                    : "Sélectionnez un jour et un mois — l'année est ignorée"
                            }
                            sx={{ maxWidth: 240, ...fsx }}
                        />
                    </Box>
                );
            })()}
        </Box>
    );
}

// ── ValueEditor ────────────────────────────────────────────────────────────────
function ValueEditor({ field, operator, value, onChange, catalog, brandName }) {
    const meta = catalog?.fieldMeta?.[field] || {};
    const valueKind = meta.valueKind;
    const ui = meta.ui || {};

    const isArrayOp = ["in", "between"].includes(operator);
    const isCustomerStatusField = field === "customer.status" || field === "status";
    const isLoyaltyStatusField = field === "customer.loyalty_status" || field === "loyalty_status";
    const [tierOptions, setTierOptions] = useState([]);
    const [loadingTiers, setLoadingTiers] = useState(false);

    useEffect(() => {
        if (!isLoyaltyStatusField || !brandName) {
            setTierOptions([]);
            return;
        }
        setLoadingTiers(true);
        const query = `?brand=${encodeURIComponent(brandName)}&active=true`;
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers${query}`, {
            headers: { "X-Brand": brandName },
        })
            .then((r) => (r.ok ? r.json() : []))
            .then((items) => setTierOptions(Array.isArray(items) ? items : []))
            .catch(() => setTierOptions([]))
            .finally(() => setLoadingTiers(false));
    }, [isLoyaltyStatusField, brandName]);

    if (operator === "exists") {
        return (
            <TextField size="small" fullWidth select label="Existe ?"
                value={value === true || value === "true" ? "true" : "false"}
                onChange={(e) => onChange(e.target.value === "true")}
                sx={fsx}
            >
                <MenuItem value="true">Oui (non nul)</MenuItem>
                <MenuItem value="false">Non (nul)</MenuItem>
            </TextField>
        );
    }

    if (isCustomerStatusField) {
        return (
            <TextField size="small" fullWidth select label="Valeur"
                InputLabelProps={{ shrink: true }}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                sx={fsx}
                helperText="Choisissez un statut"
            >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
        );
    }

    if (isLoyaltyStatusField) {
        const isMultiSelectOp = operator === "in";
        const selectedValues = isMultiSelectOp
            ? (Array.isArray(value) ? value : [value].filter(Boolean))
            : (typeof value === "string" ? value : "");

        return (
            <TextField size="small" fullWidth select
                label={loadingTiers ? "Chargement des statuts fidélité…" : "Valeur"}
                InputLabelProps={{ shrink: true }}
                value={selectedValues}
                onChange={(e) => {
                    const nextValue = e.target.value;
                    onChange(isMultiSelectOp
                        ? (Array.isArray(nextValue) ? nextValue : [nextValue].filter(Boolean))
                        : nextValue);
                }}
                sx={fsx}
                SelectProps={{
                    multiple: isMultiSelectOp,
                    displayEmpty: true,
                    renderValue: (selected) => {
                        const byKey = new Map(tierOptions.map((t) => [String(t.key), t.name || String(t.key)]));
                        if (!isMultiSelectOp) return byKey.get(String(selected)) || selected || "Sélectionnez un statut fidélité";
                        const selectedKeys = Array.isArray(selected) ? selected : [];
                        if (selectedKeys.length === 0) return "Sélectionnez un ou plusieurs statuts fidélité";
                        return selectedKeys.map((key) => byKey.get(String(key)) || String(key)).join(", ");
                    },
                }}
                helperText={loadingTiers
                    ? "Récupération des niveaux de fidélité…"
                    : `${tierOptions.length} statut(s) fidélité disponible(s)`}
            >
                {tierOptions.length === 0 && !loadingTiers && (
                    <MenuItem value="" disabled>Aucun statut fidélité disponible</MenuItem>
                )}
                {tierOptions.map((tier) => (
                    <MenuItem key={tier.id || tier.key} value={String(tier.key)}>
                        {tier.name || tier.key}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    if (isArrayOp) {
        if (operator === "between") {
            const arr = Array.isArray(value) ? value : ["", ""];
            const isNum = ["int","float","number"].includes(valueKind);
            return (
                <Box display="flex" gap={1}>
                    <TextField size="small" fullWidth type={isNum ? "number" : "text"} label="De"
                        value={arr[0] ?? ""}
                        onChange={(e) => { const v = e.target.value; onChange([isNum && v !== "" ? Number(v) : v, arr[1]]); }}
                        sx={fsx}
                    />
                    <TextField size="small" fullWidth type={isNum ? "number" : "text"} label="À"
                        value={arr[1] ?? ""}
                        onChange={(e) => { const v = e.target.value; onChange([arr[0], isNum && v !== "" ? Number(v) : v]); }}
                        sx={fsx}
                    />
                </Box>
            );
        }
        const displayVal = Array.isArray(value) ? value.join(", ") : (value ?? "");
        return (
            <TextField size="small" fullWidth
                label="Valeurs (séparées par virgule)"
                value={displayVal}
                onChange={(e) => {
                    const parts = e.target.value.split(",").map((s) => {
                        const trimmed = s.trim();
                        const num = Number(trimmed);
                        return trimmed !== "" && !isNaN(num) ? num : trimmed;
                    });
                    onChange(parts);
                }}
                helperText="Ex: ACTIVE, INACTIVE"
                sx={fsx}
            />
        );
    }

    if (valueKind === "enum" && ui.widget === "select") {
        return (
            <TextField size="small" fullWidth select label="Valeur"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                sx={fsx}
            >
                {ui.options?.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </TextField>
        );
    }

    if (valueKind === "int" || valueKind === "float" || valueKind === "number") {
        return (
            <TextField size="small" fullWidth type="number" label="Valeur"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                sx={fsx}
            />
        );
    }

    if (valueKind === "date" || valueKind === "datetime") {
        const presets = catalog?.valuePresets?.[valueKind] || [];
        const isSystemObj = value !== null && typeof value === "object" && !Array.isArray(value);
        const rawDisplay = isSystemObj ? JSON.stringify(value) : (value ?? "");
        const matchedPreset = presets.find((p) =>
            isSystemObj ? JSON.stringify(p.value) === JSON.stringify(value) : false
        );
        const presetSelectVal = matchedPreset ? matchedPreset.id : "__custom__";
        const showManual = presets.length === 0 || presetSelectVal === "__custom__";

        return (
            <Box display="flex" flexDirection="column" gap={1}>
                {presets.length > 0 && (
                    <TextField size="small" fullWidth select
                        label={`Preset (${valueKind})`}
                        value={presetSelectVal}
                        onChange={(e) => {
                            if (e.target.value === "__custom__") {
                                onChange("");
                            } else {
                                const preset = presets.find((p) => p.id === e.target.value);
                                if (preset) onChange(preset.value);
                            }
                        }}
                        sx={fsx}
                    >
                        {presets.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                        ))}
                        <MenuItem value="__custom__"><em>Valeur personnalisée…</em></MenuItem>
                    </TextField>
                )}
                {showManual && (
                    <TextField size="small" fullWidth
                        type={!isSystemObj ? (ui.widget === "datetime" ? "datetime-local" : "date") : "text"}
                        InputLabelProps={{ shrink: true }}
                        label={`Valeur (${valueKind})`}
                        value={rawDisplay}
                        onChange={(e) => {
                            let val = e.target.value;
                            try { if (val.startsWith("{")) val = JSON.parse(val); } catch (_) { }
                            onChange(val);
                        }}
                        helperText={
                            ui.widget === "datetime"
                                ? 'Ex: 2026-01-01T00:00:00, ou {"$system": "now"}'
                                : "Ex: 2026-01-01"
                        }
                        sx={fsx}
                    />
                )}
            </Box>
        );
    }

    // Default text
    const displayValFallback = value !== null && typeof value === "object" && !Array.isArray(value)
        ? JSON.stringify(value)
        : (value ?? "");

    return (
        <TextField size="small" fullWidth label="Valeur"
            value={displayValFallback}
            onChange={(e) => {
                let val = e.target.value;
                try { if (val.startsWith("{")) val = JSON.parse(val); } catch (_) { }
                onChange(val);
            }}
            sx={fsx}
        />
    );
}

// ── GroupBox ──────────────────────────────────────────────────────────────────
const GT_OPTIONS_SEL = [
    { k: "and", l: "ET",  tip: "Toutes les conditions doivent être vraies", bg: COLOR.main },
    { k: "or",  l: "OU",  tip: "Au moins une condition doit être vraie",    bg: "#f59e0b" },
    { k: "not", l: "NON", tip: "Aucune condition ne doit être vraie",        bg: "#dc2626" },
];
const DEPTH_BG_SEL = ["#eef9ff","#fefce8","#f0fdf4","#fdf4ff","#fff1f2"];

function SelectorGroupBox({ item, onUpdateGroup, onRemoveGroup, fieldOptions, operatorOptions, operatorLabel, sourceLabel, catalog, level = 0 }) {
    const conds = item.conditions || [];
    const groupType = item.groupType || item.groupCombinator || "and";
    const gtMeta = GT_OPTIONS_SEL.find((o) => o.k === groupType) || GT_OPTIONS_SEL[0];
    const accentColor = gtMeta.bg;
    const bgColor = DEPTH_BG_SEL[Math.min(level, DEPTH_BG_SEL.length - 1)];

    const setGroupType = (gt) => onUpdateGroup(item.id, { groupType: gt, groupCombinator: gt });

    const addCond = () => onUpdateGroup(item.id, {
        conditions: [...conds, { id: `c_${Date.now()}`, type: "condition", field: "", operator: "eq", value: "" }],
    });

    const addSubGroup = () => onUpdateGroup(item.id, {
        conditions: [...conds, {
            id: `g_${Date.now()}`, type: "group", groupType: "and", groupCombinator: "and",
            conditions: [{ id: `c_${Date.now() + 1}`, type: "condition", field: "", operator: "eq", value: "" }],
        }],
    });

    const removeChild = (id) => onUpdateGroup(item.id, { conditions: conds.filter((c) => c.id !== id) });

    const updateChild = (id, patch) => onUpdateGroup(item.id, {
        conditions: conds.map((c) => c.id === id ? { ...c, ...patch } : c),
    });

    // For nested group updates (recursive): patch the child group's conditions
    const updateSubGroup = (subId, patch) => onUpdateGroup(item.id, {
        conditions: conds.map((c) => c.id === subId ? { ...c, ...patch } : c),
    });

    return (
        <Box sx={{
            borderRadius: 2,
            border: `1px solid ${accentColor}30`,
            borderLeft: `4px solid ${accentColor}`,
            bgcolor: bgColor,
            p: 2,
            position: "relative",
            boxShadow: `0 1px 4px ${accentColor}18`,
        }}>
            {level > 0 && (
                <Typography sx={{ position: "absolute", top: 6, right: 10, fontSize: 9, fontWeight: 700, color: `${accentColor}80`, textTransform: "uppercase", letterSpacing: 1 }}>
                    Niveau {level + 1}
                </Typography>
            )}

            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <FolderOpenIcon sx={{ fontSize: 18, color: accentColor }} />
                    <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: `1.5px solid ${accentColor}50`, bgcolor: "#fff", boxShadow: `0 1px 3px ${accentColor}20` }}>
                        {GT_OPTIONS_SEL.map(({ k, l, tip, bg }) => (
                            <Tooltip key={k} title={tip}>
                                <Box onClick={() => setGroupType(k)} sx={{
                                    px: 2, py: 0.5, cursor: "pointer", fontSize: 11, fontWeight: 800,
                                    bgcolor: groupType === k ? bg : "transparent",
                                    color: groupType === k ? "#fff" : "#64748b",
                                    transition: "all .2s", userSelect: "none",
                                    "&:hover": { bgcolor: groupType === k ? bg : "#f8fafc", color: groupType === k ? "#fff" : "#334155" },
                                }}>{l}</Box>
                            </Tooltip>
                        ))}
                    </Box>
                    {groupType === "not" && <Typography fontSize={11} color="#dc2626" fontWeight={600} ml={1}>⚠️ Inversion</Typography>}
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                    {(groupType !== "not" || conds.length === 0) && (
                        <>
                            <Button size="small" variant="outlined" onClick={addCond}
                                sx={{ fontSize: 11, textTransform: "none", color: COLOR.main, borderColor: `${COLOR.main}50`, py: 0.2, px: 1, minWidth: 0, bgcolor: "#fff" }}>
                                + Filtre
                            </Button>
                            <Button size="small" variant="outlined" onClick={addSubGroup}
                                sx={{ fontSize: 11, textTransform: "none", color: "#7c3aed", borderColor: "#7c3aed50", py: 0.2, px: 1, minWidth: 0, bgcolor: "#fff" }}>
                                + Sous-groupe
                            </Button>
                        </>
                    )}
                    <IconButton size="small" onClick={() => onRemoveGroup(item.id)} sx={{ color: "#dc2626", ml: 1, bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ pl: 1, borderLeft: `2px solid ${accentColor}20` }}>
                {conds.length === 0 ? (
                    <Typography fontSize={12} color="text.secondary" textAlign="center" py={2} fontStyle="italic">
                        Ce groupe est vide. Ajoutez des filtres ou sous-groupes.
                    </Typography>
                ) : (
                    conds.map((child, idx) => {
                        if (child.type === "group") {
                            return (
                                <React.Fragment key={child.id}>
                                    {idx > 0 && <Box sx={{ height: "1px", bgcolor: "#e2e8f0", my: 1.5 }} />}
                                    <SelectorGroupBox
                                        item={child}
                                        onUpdateGroup={updateSubGroup}
                                        onRemoveGroup={removeChild}
                                        level={level + 1}
                                        fieldOptions={fieldOptions}
                                        operatorOptions={operatorOptions}
                                        operatorLabel={operatorLabel}
                                        sourceLabel={sourceLabel}
                                        catalog={catalog}
                                    />
                                </React.Fragment>
                            );
                        }
                        const isBday = child.field === "customer.birthdate";
                        const ns = child.field ? child.field.split(".")[0] : null;
                        const nsColor = ns === "customer" ? "#0369a1" : ns === "system" ? "#7c3aed" : "#e2e8f0";
                        return (
                            <React.Fragment key={child.id}>
                                {idx > 0 && <Box sx={{ height: "1px", bgcolor: "#e2e8f0", my: 1.5 }} />}
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e2e8f0", borderLeft: `4px solid ${isBday ? COLOR.main : nsColor}` }}>
                                    <Box display="flex" gap={1} alignItems="flex-start">
                                        <Box flex={1} display="flex" flexDirection="column" gap={1}>
                                            <Box display="flex" gap={1} flexWrap="wrap">
                                                <Box flex={2} minWidth={160}>
                                                    <Autocomplete freeSolo forcePopupIcon size="small" options={fieldOptions} value={child.field || null}
                                                        onChange={(_, v) => { const f = v || ""; updateChild(child.id, { field: f, value: "", _birthdayMode: f === "customer.birthdate" ? "today" : undefined }); }}
                                                        onInputChange={(_, v, r) => { if (r === "input") updateChild(child.id, { field: v, value: "", _birthdayMode: v === "customer.birthdate" ? "today" : undefined }); }}
                                                        renderInput={(p) => <TextField {...p} label="Champ" size="small" sx={fsx} />}
                                                        renderOption={(p, o) => { const [ns2] = o.split("."); return <li {...p}><Typography fontSize={12} fontWeight={700} color={ns2 === "customer" ? "#0369a1" : ns2 === "system" ? "#7c3aed" : "#334155"}>{o}</Typography></li>; }}
                                                    />
                                                </Box>
                                                {!isBday && (
                                                    <Box flex={1} minWidth={120}>
                                                        <TextField select size="small" fullWidth label="Opérateur" value={child.operator || "eq"} onChange={(e) => updateChild(child.id, { operator: e.target.value })} sx={fsx}>
                                                            {operatorOptions.map((op) => <MenuItem key={op} value={op}><Typography fontSize={12}>{operatorLabel(op)}</Typography></MenuItem>)}
                                                        </TextField>
                                                    </Box>
                                                )}
                                            </Box>
                                            {isBday && <BirthdaySelector row={child} onUpdate={(patch) => updateChild(child.id, patch)} />}
                                            {child.field && !isBday && <ValueEditor field={child.field} operator={child.operator || "eq"} value={child.value} onChange={(v) => updateChild(child.id, { value: v })} catalog={catalog} />}
                                        </Box>
                                        <IconButton size="small" onClick={() => removeChild(child.id)} sx={{ color: "#dc2626", mt: 0.5, bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </React.Fragment>
                        );
                    })
                )}
            </Box>
        </Box>
    );
}


// ── SelectorBuilder ────────────────────────────────────────────────────────────


// ── JoinerToggle ─────────────────────────────────────────────────────────────
function JoinerToggle({ value = "and", onChange }) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" my={0.75} gap={0.5}>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
            <Box sx={{
                display: "flex", borderRadius: 99, overflow: "hidden",
                border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}>
                {[{ key: "and", label: "ET", activeColor: COLOR.main, activeText: "#fff" },
                  { key: "or",  label: "OU", activeColor: "#f59e0b", activeText: "#fff" }].map(({ key, label, activeColor, activeText }) => (
                    <Box
                        key={key}
                        onClick={() => onChange(key)}
                        sx={{
                            px: 2, py: 0.4, cursor: "pointer",
                            fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                            bgcolor: value === key ? activeColor : "#fff",
                            color: value === key ? activeText : "#94a3b8",
                            transition: "all 0.15s ease",
                            userSelect: "none",
                            "&:hover": { bgcolor: value === key ? activeColor : "#f8fafc", color: value === key ? activeText : "#475569" },
                        }}
                    >
                        {label}
                    </Box>
                ))}
            </Box>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
        </Box>
    );
}

const SelectorBuilder = ({ brandName, value, onChange }) => {
    const [catalog, setCatalog] = useState(null);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [combinator, setCombinator] = useState("and");
    const [rows, setRows] = useState([]);

    useEffect(() => {
        setLoadingCatalog(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/internal-jobs/ui-options/selector-fields`, {
            headers: brandName ? { "X-Brand": brandName } : {},
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => { if (data) setCatalog(data); })
            .catch(() => { })
            .finally(() => setLoadingCatalog(false));
    }, [brandName]);

    useEffect(() => {
        if (!catalog) return;
        const parsed = parseSelectorJson(value);
        setCombinator(parsed.combinator);
        setRows(parsed.rows);
    }, [catalog]);

    const recomputeCombinator = (nextRows) => {
        const joiners = nextRows.slice(1).map((r) => r.joiner || "and");
        const orCount = joiners.filter((j) => j === "or").length;
        return joiners.length > 0 && orCount > joiners.length / 2 ? "or" : "and";
    };

    const propagate = (newCombinator, newRows) => {
        onChange(buildSelectorJson(newCombinator, newRows));
    };

    const [topCombinator, setTopCombinator] = useState("and");

    const emit = (next, tc = topCombinator) => {
        setCombinator(tc);
        setRows(next);
        propagate(tc, next);
    };

    const changeTopCombinator = (tc) => {
        setTopCombinator(tc);
        setCombinator(tc);
        setRows(rows);
        propagate(tc, rows);
    };

    const addRow = () => { if (!catalog) return; emit([...rows, { id: Date.now(), type: 'condition', field: '', operator: 'eq', value: '', joiner: 'and' }]); };

    const addGroup = () => {
        if (!catalog) return;
        emit([...rows, {
            id: `g_${Date.now()}`, type: 'group', joiner: 'and',
            conditions: [{ id: Date.now(), type: 'condition', field: '', operator: 'eq', value: '' }],
        }]);
    };

    const removeRow = (id) => emit(rows.filter((r) => r.id !== id));

    const updateRow = (id, patch) => emit(rows.map((r) => r.id === id ? { ...r, ...patch } : r));

    const updateGroup = (groupId, patch) => emit(rows.map((r) => r.id === groupId ? { ...r, ...patch } : r));

    if (loadingCatalog) {
        return (
            <Box display="flex" alignItems="center" gap={1} p={2}
                sx={{ borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <CircularProgress size={16} />
                <Typography fontSize={12} color="text.secondary">Chargement du catalogue…</Typography>
            </Box>
        );
    }

    if (!catalog) {
        return (
            <Typography fontSize={12} color="error">
                Impossible de charger le catalogue de sélecteurs. Vérifiez la connexion au backend.
            </Typography>
        );
    }

    const fieldOptions = catalog.items || [];
    const operatorOptions = ["eq", "neq", "gt", "gte", "lt", "lte", "between", "in", "contains", "exists"];
    const combinatorOptions = ["and", "or", "not"];

    const sourceLabel = (ns) => {
        if (ns === "customer") return "Profil client";
        if (ns === "system") return "Valeur système (calculée à l'exécution)";
        return ns;
    };

    const combinatorLabel = (c) => {
        if (c === "and") return "Toutes (AND)";
        if (c === "or") return "Au moins une (OR)";
        if (c === "not") return "Aucune (NOT)";
        return c.toUpperCase();
    };

    const operatorLabel = (op) => {
        const labels = {
            eq: "= (égal)", neq: "≠ (différent)", gt: "> (supérieur)", gte: "≥ (supérieur ou égal)",
            lt: "< (inférieur)", lte: "≤ (inférieur ou égal)", between: "Entre [lo, hi]",
            in: "Dans la liste", contains: "Contient", exists: "Existe",
        };
        return labels[op] || op;
    };

    // Logic summary
    const allJoiners = rows.slice(1).map((r) => r.joiner || "and");
    const hasOr  = allJoiners.includes("or");
    const hasAnd = allJoiners.includes("and");
    const isMixed = hasOr && hasAnd;
    const hasGroup = rows.some((r) => r.type === 'group');

    return (
        <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${COLOR.main}30`, bgcolor: "#fafbfd" }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <FilterAltIcon sx={{ fontSize: 18, color: COLOR.main }} />
                    <Typography fontSize={13} fontWeight={700} color="#334155">Filtre de sélection des clients</Typography>
                    {hasGroup && <Chip label="( ) Groupes actifs" size="small" sx={{ fontSize: 10, fontWeight: 700, height: 20, bgcolor: "#f3e8ff", color: "#7c3aed", border: "1px solid #d8b4fe" }} />}
                </Box>

                {/* Global combinator toggle */}
                {rows.length > 1 && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize={11} color="text.secondary" fontWeight={600}>Relier par :</Typography>
                        <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: `1px solid ${COLOR.main}40`, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
                            {[{ k: "and", l: "ET (AND)", ac: COLOR.main }, { k: "or", l: "OU (OR)", ac: "#f59e0b" }].map(({ k, l, ac }) => (
                                <Box key={k} onClick={() => changeTopCombinator(k)} sx={{
                                    px: 2, py: 0.5, cursor: "pointer", fontSize: 11, fontWeight: 800,
                                    bgcolor: topCombinator === k ? ac : "#fff",
                                    color: topCombinator === k ? "#fff" : "#94a3b8",
                                    transition: "all .15s", userSelect: "none",
                                    "&:hover": { bgcolor: topCombinator === k ? ac : "#f8fafc", color: topCombinator === k ? "#fff" : "#475569" },
                                }}>{l}</Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Box display="flex" gap={0.75}>
                    <Tooltip title="Ajouter un filtre">
                        <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addRow}
                            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 12, color: COLOR.main }}>
                            Filtre
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ajouter un groupe ( … )">
                        <Button size="small" startIcon={<FolderOpenIcon />} onClick={addGroup}
                            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 12, color: "#7c3aed", border: "1px solid #d8b4fe", bgcolor: "#faf5ff" }}>
                            Groupe
                        </Button>
                    </Tooltip>
                </Box>
            </Box>



            {/* Empty state */}
            {rows.length === 0 && (
                <Typography fontSize={12} color="text.secondary" textAlign="center" py={1.5}>
                    Aucun filtre — tous les clients seront ciblés.
                </Typography>
            )}

            {/* Filter rows */}
            {rows.map((row, idx) => {
                const isBirthdayField = row.type !== 'group' && row.field === "customer.birthdate";
                const ns = row.field ? row.field.split(".")[0] : null;
                const nsColor = ns === "customer" ? "#0369a1" : ns === "system" ? "#7c3aed" : "#e2e8f0";
                const shared = { fieldOptions, operatorOptions, operatorLabel, sourceLabel, catalog };

                if (row.type === 'group') {
                    return (
                        <React.Fragment key={row.id}>
                            {idx > 0 && <Box sx={{ height: "1px", bgcolor: "#e2e8f0", my: 1.5 }} />}
                            <SelectorGroupBox item={row} onUpdateGroup={updateGroup} onRemoveGroup={removeRow} level={0} {...{ fieldOptions, operatorOptions, operatorLabel, sourceLabel, catalog }} />
                        </React.Fragment>
                    );
                }

                return (
                    <React.Fragment key={row.id}>
                        {idx > 0 && <Box sx={{ height: "1px", bgcolor: "#e2e8f0", my: 1.5 }} />}
                    <Box sx={{
                        p: 1.5, mb: 0, borderRadius: 2, bgcolor: "#fff",
                        border: isBirthdayField ? `1.5px solid ${COLOR.main}50` : "1px solid #e2e8f0",
                        borderLeft: `3px solid ${isBirthdayField ? COLOR.main : nsColor}`,
                        transition: "border-color 0.2s",
                    }}>

                        <Box display="flex" gap={1} alignItems="flex-start">
                            {/* Row number */}
                            <Box sx={{
                                width: 22, height: 22, borderRadius: "50%", flexShrink: 0, mt: 0.75,
                                bgcolor: `${COLOR.main}18`, display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <Typography fontSize={10} fontWeight={800} color={COLOR.main}>{idx + 1}</Typography>
                            </Box>

                            <Box flex={1} display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    {/* Field selector */}
                                    <Box flex={2} minWidth={180}>
                                        <Autocomplete
                                            freeSolo
                                            forcePopupIcon
                                            size="small"
                                            options={fieldOptions}
                                            value={row.field || null}
                                            onChange={(_, newVal) => {
                                                const newField = newVal || "";
                                                updateRow(row.id, {
                                                    field: newField,
                                                    value: "",
                                                    _birthdayMode: newField === "customer.birthdate" ? "today" : undefined,
                                                });
                                            }}
                                            onInputChange={(_, newInput, reason) => {
                                                if (reason === "input") {
                                                    updateRow(row.id, {
                                                        field: newInput,
                                                        value: "",
                                                        _birthdayMode: newInput === "customer.birthdate" ? "today" : undefined,
                                                    });
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Champ" size="small" sx={fsx} placeholder="customer.status" />
                                            )}
                                            renderOption={(props, option) => {
                                                const [ns] = option.split(".");
                                                const meta = catalog.fieldMeta?.[option];
                                                return (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography fontSize={12} fontWeight={700} color={
                                                                ns === "customer" ? "#0369a1" :
                                                                    ns === "system" ? "#7c3aed" : "#334155"
                                                            }>
                                                                {option}
                                                                {option === "customer.birthdate" && (
                                                                    <CakeIcon sx={{ fontSize: 12, ml: 0.5, color: COLOR.main, verticalAlign: "middle" }} />
                                                                )}
                                                            </Typography>
                                                            <Typography fontSize={10} color="text.secondary">
                                                                {sourceLabel(ns)}{meta?.valueKind ? ` · ${meta.valueKind}` : ""}
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                );
                                            }}
                                        />
                                    </Box>

                                    {/* Operator — hidden for birthday (toggle replaces it) */}
                                    {!isBirthdayField && (
                                        <Box flex={1} minWidth={130}>
                                            <TextField
                                                select size="small" fullWidth label="Opérateur"
                                                value={row.operator || "eq"}
                                                onChange={(e) => updateRow(row.id, { operator: e.target.value })}
                                                sx={fsx}
                                            >
                                                {operatorOptions.map((op) => (
                                                    <MenuItem key={op} value={op}>
                                                        <Typography fontSize={12}>{operatorLabel(op)}</Typography>
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                    )}
                                </Box>

                                {/* Birthday dedicated UI */}
                                {isBirthdayField && (
                                    <BirthdaySelector
                                        row={row}
                                        onUpdate={(patch) => updateRow(row.id, patch)}
                                    />
                                )}

                                {/* Generic value editor — only for non-birthday fields */}
                                {row.field && !isBirthdayField && (
                                    <ValueEditor
                                        field={row.field}
                                        operator={row.operator || "eq"}
                                        value={row.value}
                                        onChange={(val) => updateRow(row.id, { value: val })}
                                        catalog={catalog}
                                        brandName={brandName}
                                    />
                                )}

                                {/* Namespace hint */}
                                {row.field && (
                                    <Typography fontSize={10} color="text.secondary" px={0.5}>
                                        {row.field.startsWith("customer.")
                                            ? "📋 Champ du profil client"
                                            : row.field.startsWith("system.")
                                                ? "⚙️ Valeur dynamique calculée à l'exécution"
                                                : ""}
                                    </Typography>
                                )}
                            </Box>

                            {/* Delete */}
                            <IconButton size="small" onClick={() => removeRow(row.id)} sx={{ color: "#dc2626", mt: 0.5 }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export default SelectorBuilder;
